/**
 * EquipePage — Gestor gerencia equipe do cliente (B1.8 §7 — Gestor only).
 *
 * Backend GET /api/v1/cliente/equipe lista TODOS vínculos ativos (Gestor + Portaria).
 * POST /api/v1/cliente/equipe/portaria cria Portaria nova (Gestor only).
 * DELETE /api/v1/cliente/equipe/portaria/:user_id remove (soft delete CAR).
 *
 * Gestor pode adicionar/remover SÓ Portaria (não outro Gestor — só admin
 * interno faz isso via aba admin de /admin/clientes-v2/:id).
 * Gestor não pode remover a si mesmo (anti-lockout).
 *
 * NÃO REVERTER:
 *   - Backend valida feature_key 'gerenciar_equipe' (Portaria 403)
 *   - Gestor NÃO vê opção de remover outros Gestores (UI hide + server guard)
 *   - Soft delete: vínculo readicionável depois pelo mesmo Gestor
 *   - Cadastro chama mesma engine do admin (cadastrarPortalUser) — envia
 *     convite por email se user novo, vincula direto se já existe
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert, Badge, Button, EmptyState, Input, Label, useConfirmDialog } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { useCliente } from '@/contexts/ClienteContext';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';
import { DefinirSenhaPortalDialog } from '@/components/admin/DefinirSenhaPortalDialog';
import {
  useAdicionarPortaria,
  useAdicionarPortariaSemEmail,
  useClienteDashboard,
  useDefinirSenhaPortaria,
  useEquipe,
  useRemoverPortaria,
  type EquipeMember,
} from '@/lib/cliente-portal-store';
import { ClienteApiError } from '@/lib/cliente-api';
import { mensagemErro } from '@/lib/mensagem-publica';

export function EquipePage() {
  const { clienteAtivo } = useCliente();
  const { user: meuUser } = useClienteAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirmar = useConfirmDialog();
  const dashQuery = useClienteDashboard();
  const podeGerenciar = dashQuery.data?.permissoes.gerenciar_equipe ?? false;

  useEffect(() => {
    if (dashQuery.isSuccess && !podeGerenciar) {
      toast.show('Só quem é gestor pode mexer na equipe.', { variant: 'info' });
      navigate('/cliente/dashboard', { replace: true });
    }
  }, [dashQuery.isSuccess, podeGerenciar, navigate, toast]);

  const { data, isLoading, isError, error } = useEquipe();
  const adicionar = useAdicionarPortaria();
  const remover = useRemoverPortaria();

  const [novoEmail, setNovoEmail] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // 26/10/2026 (Matheus): a portaria sem e-mail entra com usuário e senha, criados aqui pelo síndico, sem convite.
  const [semEmail, setSemEmail] = useState(false);
  const [novoUsuario, setNovoUsuario] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [senhaDe, setSenhaDe] = useState<EquipeMember | null>(null);
  const adicionarSemEmail = useAdicionarPortariaSemEmail();
  const definirSenha = useDefinirSenhaPortaria();

  const fraseDoServidor = (err: unknown, padrao: string) =>
    (err instanceof ClienteApiError ? err.fraseDoServidor() : undefined) ?? mensagemErro(err, padrao);

  async function handleAdicionarSemEmail() {
    if (!novoNome.trim() || !novoUsuario.trim() || !novaSenha) {
      setErrorMsg('Nome, usuário e senha são obrigatórios.');
      return;
    }
    if (novaSenha.length < 8) {
      setErrorMsg('A senha precisa de pelo menos 8 caracteres.');
      return;
    }
    try {
      const r = await adicionarSemEmail.mutateAsync({ nome: novoNome.trim(), usuario: novoUsuario.trim(), senha: novaSenha });
      toast.show(`${novoNome.trim()} pode entrar com o usuário ${r.usuario} e a senha que você criou.`, { variant: 'success' });
      setNovoNome('');
      setNovoUsuario('');
      setNovaSenha('');
    } catch (err) {
      setErrorMsg(fraseDoServidor(err, 'Não conseguimos criar o acesso agora. Tente de novo.'));
    }
  }

  async function handleAdicionar(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (semEmail) {
      await handleAdicionarSemEmail();
      return;
    }
    if (!novoEmail.trim() || !novoNome.trim()) {
      setErrorMsg('Email e nome são obrigatórios.');
      return;
    }
    try {
      const r = await adicionar.mutateAsync({
        email: novoEmail.trim(),
        nome: novoNome.trim(),
      });
      // O aviso de e-mail não enviado NÃO conta o motivo técnico ("config STUB"
      // era texto interno vazando pro síndico) — diz o que ele precisa fazer.
      const mensagem =
        r.acao === 'criado_com_convite'
          ? r.emailEnviado
            ? `Convite enviado para ${novoEmail.trim()}.`
            : `${novoNome.trim()} foi cadastrado(a), mas o convite por e-mail não saiu. Avise a IGM para reenviar.`
          : `${novoNome.trim()} agora tem acesso como portaria.`;
      toast.show(mensagem, { variant: 'success' });
      setNovoEmail('');
      setNovoNome('');
    } catch (err) {
      if (err instanceof ClienteApiError && err.status === 409) {
        if (/colaborador-interno/i.test(err.message)) {
          setErrorMsg('Esse e-mail é de alguém da equipe IGM e não pode ser cadastrado como portaria.');
        } else {
          setErrorMsg('Essa pessoa já tem acesso a este condomínio.');
        }
        return;
      }
      setErrorMsg(mensagemErro(err, 'Não conseguimos enviar o convite agora. Tente de novo.'));
    }
  }

  async function handleRemover(m: EquipeMember) {
    const ok = await confirmar({
      title: 'Tirar o acesso desta pessoa?',
      message: `${m.nome} deixa de entrar no portal deste condomínio. Você pode dar o acesso de volta depois.`,
      confirmLabel: 'Tirar acesso',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await remover.mutateAsync(m.userId);
      toast.show(`${m.nome} não tem mais acesso ao portal.`, { variant: 'success' });
    } catch (err) {
      toast.show(mensagemErro(err, 'Não conseguimos remover o acesso agora. Tente de novo.'), {
        variant: 'error',
      });
    }
  }

  if (!podeGerenciar && dashQuery.isSuccess) return null;

  const items = data?.items ?? [];
  const gestores = items.filter((m) => m.role === 'gestor');
  const portarias = items.filter((m) => m.role === 'portaria');

  return (
    <PortalPage
      testId="equipe-page"
      title="Equipe"
      subtitle={
        clienteAtivo
          ? `Quem entra no portal de ${clienteAtivo.nome}. Você cadastra e remove portarias; para mexer em gestores, fale com a IGM.`
          : 'Quem entra no portal deste condomínio.'
      }
    >
      {isError ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar a equipe agora. Tente atualizar a página.')}
        </Alert>
      ) : !clienteAtivo || dashQuery.isLoading || isLoading ? (
        <PortalCarregando linhas={2} rotulo="Carregando a equipe…" />
      ) : (
        <>
          {/* Gestores (read-only — Gestor não gerencia outro Gestor) */}
          <section className="mb-8" data-testid="equipe-gestores">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Gestores ({gestores.length})
            </h2>
            {gestores.length === 0 ? (
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                Nenhum gestor cadastrado. Fale com a IGM — deveria haver pelo menos um.
              </p>
            ) : (
              <ul className="space-y-2">
                {gestores.map((m) => (
                  <MembroCard key={m.userId} membro={m} meuUserId={meuUser?.id ?? null} />
                ))}
              </ul>
            )}
          </section>

          {/* Portarias */}
          <section className="mb-8" data-testid="equipe-portarias">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Portarias ({portarias.length})
            </h2>
            {portarias.length === 0 ? (
              <EmptyState
                title="Nenhuma portaria com acesso ainda"
                description="Cadastre logo abaixo. Quem entra como portaria só abre e acompanha os próprios chamados — não vê orçamentos, contratos nem valores."
              />
            ) : (
              <ul className="space-y-2">
                {portarias.map((m) => (
                  <MembroCard
                    key={m.userId}
                    membro={m}
                    meuUserId={meuUser?.id ?? null}
                    onRemover={handleRemover}
                    removerLoading={remover.isPending}
                    onDefinirSenha={setSenhaDe}
                  />
                ))}
              </ul>
            )}
          </section>

          {/* Form adicionar Portaria */}
          <section
            className="rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface"
            data-testid="equipe-adicionar"
          >
            <h2 className="mb-1 text-base font-semibold text-ink-1 dark:text-ink-dark-1">
              Dar acesso a alguém da portaria
            </h2>
            <p className="mb-4 text-sm text-ink-2 dark:text-ink-dark-2">
              {semEmail
                ? 'Sem e-mail: você cria o usuário e a senha e passa para a portaria. Esqueceu a senha? Aqui, em "Definir senha".'
                : 'Quem nunca entrou no portal recebe um e-mail para criar a senha. Quem já tem cadastro na IGM passa a ver este condomínio na hora.'}
            </p>
            <form onSubmit={handleAdicionar} className="space-y-3">
              <label className="flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-ink-1 dark:text-ink-dark-1">
                <input
                  type="checkbox"
                  checked={semEmail}
                  onChange={(e) => {
                    setSemEmail(e.target.checked);
                    setErrorMsg(null);
                  }}
                  className="h-5 w-5"
                  data-testid="equipe-sem-email"
                />
                <span>
                  <strong>Sem e-mail:</strong> criar usuário e senha
                </span>
              </label>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="port-nome" required>
                    Nome
                  </Label>
                  <Input
                    id="port-nome"
                    value={novoNome}
                    onChange={(e) => setNovoNome(e.target.value)}
                    placeholder="Ex.: Carlos da portaria"
                    disabled={adicionar.isPending || adicionarSemEmail.isPending}
                    data-testid="equipe-novo-nome"
                  />
                </div>
                {semEmail ? (
                  <>
                    <div>
                      <Label htmlFor="port-usuario" required>
                        Usuário
                      </Label>
                      <Input
                        id="port-usuario"
                        value={novoUsuario}
                        onChange={(e) => setNovoUsuario(e.target.value.toLowerCase())}
                        placeholder="Ex.: portaria.noite"
                        autoComplete="off"
                        disabled={adicionarSemEmail.isPending}
                        data-testid="equipe-novo-usuario"
                      />
                    </div>
                    <div>
                      <Label htmlFor="port-senha" required>
                        Senha
                      </Label>
                      <Input
                        id="port-senha"
                        type="password"
                        value={novaSenha}
                        onChange={(e) => setNovaSenha(e.target.value)}
                        autoComplete="new-password"
                        disabled={adicionarSemEmail.isPending}
                        data-testid="equipe-nova-senha"
                      />
                      <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">Pelo menos 8 caracteres.</p>
                    </div>
                  </>
                ) : (
                  <div>
                    <Label htmlFor="port-email" required>
                      E-mail
                    </Label>
                    <Input
                      id="port-email"
                      type="email"
                      value={novoEmail}
                      onChange={(e) => setNovoEmail(e.target.value)}
                      placeholder="carlos@exemplo.com.br"
                      disabled={adicionar.isPending}
                      data-testid="equipe-novo-email"
                    />
                  </div>
                )}
              </div>
              {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full sm:w-auto"
                  disabled={adicionar.isPending || adicionarSemEmail.isPending}
                  data-testid="equipe-adicionar-submit"
                >
                  {semEmail
                    ? adicionarSemEmail.isPending
                      ? 'Criando…'
                      : 'Criar acesso'
                    : adicionar.isPending
                      ? 'Enviando…'
                      : 'Enviar convite'}
                </Button>
              </div>
            </form>
          </section>
          <DefinirSenhaPortalDialog
            aberta={senhaDe !== null}
            nome={senhaDe?.nome ?? ''}
            usuario={senhaDe?.usuario ?? null}
            aoFechar={() => setSenhaDe(null)}
            salvar={async (senha) => {
              if (!senhaDe) return;
              try {
                await definirSenha.mutateAsync({ userId: senhaDe.userId, senha });
              } catch (err) {
                throw new Error(fraseDoServidor(err, 'Não conseguimos salvar a senha agora. Tente de novo.'));
              }
            }}
            aoSalvar={() => {
              toast.show(`Senha de ${senhaDe?.nome ?? 'a portaria'} definida. Passe para ela a senha nova.`, { variant: 'success' });
              setSenhaDe(null);
            }}
          />
        </>
      )}
    </PortalPage>
  );
}

interface MembroCardProps {
  membro: EquipeMember;
  meuUserId: string | null;
  onRemover?: (m: EquipeMember) => void;
  removerLoading?: boolean;
  /** Só para a portaria sem e-mail: o síndico define a senha (não há "esqueci a senha" sem e-mail). */
  onDefinirSenha?: (m: EquipeMember) => void;
}

function MembroCard({ membro: m, meuUserId, onRemover, removerLoading, onDefinirSenha }: MembroCardProps) {
  const isVoce = meuUserId === m.userId;
  return (
    <li
      className="flex items-center justify-between gap-3 rounded-xl border border-line bg-paper-surface p-3 dark:border-line-dark dark:bg-paper-dark-surface"
      data-testid={`equipe-membro-${m.userId}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 font-medium text-ink-1 dark:text-ink-dark-1">
          {m.nome}
          {isVoce && (
            <Badge variant="info" size="sm">
              Você
            </Badge>
          )}
          {m.isPrincipal && (
            <Badge variant="warning" size="sm">
              Contato principal
            </Badge>
          )}
          {m.status !== 'ativo' && (
            <Badge variant="neutral" size="sm">
              {labelStatusAcesso(m.status)}
            </Badge>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-ink-2 dark:text-ink-dark-2">
          {m.semEmail ? `usuário ${m.usuario ?? ''} · sem e-mail` : m.email}
        </div>
      </div>
      {onDefinirSenha && m.semEmail && (
        <Button variant="ghost" size="sm" onClick={() => onDefinirSenha(m)} data-testid={`equipe-senha-${m.userId}`}>
          Definir senha
        </Button>
      )}
      {onRemover && !isVoce && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => onRemover(m)}
          disabled={removerLoading}
          data-testid={`equipe-remover-${m.userId}`}
        >
          Tirar acesso
        </Button>
      )}
    </li>
  );
}

/** `users.status` é código do banco ('convidado', 'inativo'…) — vira português. */
function labelStatusAcesso(status: string): string {
  const map: Record<string, string> = {
    convidado: 'Convite enviado',
    pendente: 'Convite enviado',
    inativo: 'Acesso desativado',
    bloqueado: 'Acesso bloqueado',
  };
  return map[status] ?? 'Acesso pendente';
}
